import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import ChevronSVG from '../../../../../images/svg/icons8-expand-arrow';
import Text from '../../../../global/text/text';
import { Popover } from '../../../../global/popover/popover';
import { RoleConnectionKey } from '../../../../../api/blueprints/connection.template.types';
import { ConnectionTemplatesFormVM } from '../connection-template-form.viewmodel';
import {
    ROLE_CONNECTIONS_SECTION_TEXT,
    ROLE_CONNECTION_HOST_TEXT,
    ROLE_CONNECTION_PRESENTER_TEXT,
    ROLE_CONNECTION_SUPPORT_TEXT,
} from '../connection-template-form.texts';
import { CHOOSE_CONNECTION_OPTION_ID, IRoleConnectionOption } from './role-connections.utils';
import '../../../../_events/_manage-event/shared/_connection-dropdown/connection-dropdown.scss';
import './connection-template-role-connections.scss';
import { unselectedOptionValue } from '../../../../_events/_manage-event/shared/_connection-dropdown/connection-dropdown.presentation';

const ROLE_ROWS: { key: RoleConnectionKey; label: typeof ROLE_CONNECTION_PRESENTER_TEXT }[] = [
    { key: 'presenter', label: ROLE_CONNECTION_PRESENTER_TEXT },
    { key: 'host', label: ROLE_CONNECTION_HOST_TEXT },
    { key: 'support', label: ROLE_CONNECTION_SUPPORT_TEXT },
];

type RoleConnectionDropdownProps = {
    options: IRoleConnectionOption[];
    selectedId: string;
    selectedLabel: string;
    onChange: (optionId: string) => void;
};

const RoleConnectionDropdown = ({
    options,
    selectedId,
    selectedLabel,
    onChange,
}: RoleConnectionDropdownProps): JSX.Element => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const hasChoices = options.some(option => option.id !== CHOOSE_CONNECTION_OPTION_ID);
    const isSelected = Boolean(selectedId);

    const handleSelect = (optionId: string): void => {
        onChange(optionId);
        setIsOpen(false);
    };

    return (
        <div
            className={classnames('connection-dropdown', {
                'connection-dropdown--selected': isSelected,
                'connection-dropdown--disabled': !hasChoices,
                'connection-dropdown--active': isOpen,
            })}
            ref={buttonRef}
            data-testid='role-connection-dropdown'
        >
            <div
                className='connection-dropdown__wrapper'
                onClick={() => {
                    if (hasChoices) setIsOpen(open => !open);
                }}
            >
                <div className='connection-dropdown__team'>
                    <Text
                        className={classnames('connection-dropdown__label', {
                            'connection-dropdown__label--visible': isSelected || isOpen,
                        })}
                        message={isSelected ? selectedLabel : unselectedOptionValue}
                        withEllipsis
                    />
                    <ChevronSVG />
                </div>
            </div>
            <Popover
                padding={0}
                isOpen={isOpen}
                buttonRef={buttonRef}
                onClickOutside={() => setIsOpen(false)}
            >
                <div className='connection-dropdown__options'>
                    {options.map(option => (
                        <div
                            className={classnames('connection-dropdown__option', {
                                'connection-dropdown__option--selected': selectedId === option.id,
                                'connection-dropdown__option--connection-group': option.isGroup,
                            })}
                            key={option.id || 'choose-connection'}
                            onClick={() => handleSelect(option.id)}
                        >
                            <Text message={option.label} withEllipsis />
                        </div>
                    ))}
                </div>
            </Popover>
        </div>
    );
};

export type ConnectionTemplateRoleConnectionsProps = {
    vm: ConnectionTemplatesFormVM;
};

export const ConnectionTemplateRoleConnections = observer(({ vm }: ConnectionTemplateRoleConnectionsProps): JSX.Element => {
    const options = vm.getRoleConnectionOptions(unselectedOptionValue);

    return (
        <div className='role-connections' data-testid='role-connections-section'>
            <div className='role-connections__title'>
                <FormattedMessage {...ROLE_CONNECTIONS_SECTION_TEXT} />
            </div>
            {ROLE_ROWS.map(row => (
                <div className='role-connections__row' key={row.key} data-testid={`role-connection-row-${row.key}`}>
                    <span className='role-connections__label'>
                        <FormattedMessage {...row.label} />
                    </span>
                    <RoleConnectionDropdown
                        options={options}
                        selectedId={vm.getRoleConnectionOptionId(row.key)}
                        selectedLabel={vm.getRoleConnectionLabel(row.key)}
                        onChange={optionId => vm.setRoleConnection(row.key, optionId)}
                    />
                </div>
            ))}
        </div>
    );
});
